// src/pages/dashboard/ProfilePage.js
import React from 'react';
import { ChevronRight, User, Star, Trophy, LogOut, Settings } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const ProfilePage = ({ onNavigate }) => {
  const { currentUser, userProgress, logout } = useAuth();

  const handleLogout = () => {
    logout();
    // Navigation will be handled automatically by the auth state change
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-4">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button 
            onClick={() => onNavigate('dashboard')}
            className="bg-white p-3 rounded-full shadow-lg hover:shadow-xl transition-shadow"
          >
            <ChevronRight className="w-6 h-6 rotate-180" />
          </button>
          <h2 className="text-2xl font-bold text-gray-800">Perfil</h2>
          <div className="w-12"></div>
        </div>

        {/* Profile Card */}
        <div className="bg-white rounded-3xl shadow-xl p-8">
          {/* User Info */}
          <div className="text-center mb-8">
            <div className="w-24 h-24 bg-gradient-to-r from-red-600 to-red-700 rounded-full mx-auto mb-4 flex items-center justify-center">
              <User className="w-12 h-12 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-gray-800">{currentUser?.name}</h3>
            <p className="text-gray-600">{currentUser?.email}</p>
            <div className="mt-2 inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
              Membro desde {new Date(currentUser?.created_at || Date.now()).toLocaleDateString('pt-BR')}
            </div>
          </div>

          {/* Progress Stats */}
          <div className="space-y-4 mb-8">
            {/* Current Level */}
            <div className="bg-red-50 p-4 rounded-2xl">
              <div className="flex items-center justify-between mb-2">
                <span className="text-red-800 font-bold">Nível Atual</span>
                <span className="text-red-600 font-bold">HSK {userProgress.level}</span>
              </div>
              <div className="w-full bg-red-200 rounded-full h-2">
                <div 
                  className="bg-red-600 h-2 rounded-full transition-all duration-500" 
                  style={{ 
                    width: `${Math.min(100, ((userProgress.total_points % 100) / 100) * 100)}%` 
                  }}
                ></div>
              </div>
              <div className="flex justify-between text-xs text-red-600 mt-1">
                <span>{userProgress.total_points % 100} pontos</span>
                <span>{100 - (userProgress.total_points % 100)} para próximo nível</span>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-yellow-50 p-4 rounded-2xl text-center">
                <Star className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-yellow-800">{userProgress.total_points}</div>
                <div className="text-yellow-600 text-sm">Pontos</div>
              </div>
              <div className="bg-orange-50 p-4 rounded-2xl text-center">
                <Trophy className="w-8 h-8 text-orange-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-orange-800">{userProgress.streak_days}</div>
                <div className="text-orange-600 text-sm">Dias seguidos</div>
              </div>
            </div>

            {/* Detailed Stats */}
            <div className="bg-green-50 p-4 rounded-2xl">
              <h4 className="font-bold text-green-800 mb-3">Estatísticas Detalhadas</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-green-700">Lições Completas:</span>
                  <span className="font-bold text-green-800">{userProgress.completed_lessons}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-green-700">Nível HSK:</span>
                  <span className="font-bold text-green-800">{userProgress.level}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-green-700">Badges Conquistados:</span>
                  <span className="font-bold text-green-800">{userProgress.badges?.length || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-green-700">Pontos por Nível:</span>
                  <span className="font-bold text-green-800">{Math.floor(userProgress.total_points / userProgress.level)}</span>
                </div>
              </div>
            </div>

            {/* Achievements */}
            {userProgress.badges && userProgress.badges.length > 0 && (
              <div className="bg-purple-50 p-4 rounded-2xl">
                <h4 className="font-bold text-purple-800 mb-3">Conquistas Recentes</h4>
                <div className="flex gap-2 flex-wrap">
                  {userProgress.badges.slice(0, 4).map((badge, index) => (
                    <div key={index} className="bg-purple-200 p-2 rounded-xl text-center flex-1 min-w-0">
                      <Trophy className="w-6 h-6 text-purple-700 mx-auto mb-1" />
                      <p className="text-xs text-purple-800 font-semibold truncate">
                        {badge.name}
                      </p>
                      {badge.description && (
                        <p className="text-xs text-purple-600 mt-1">
                          {badge.description}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
                {userProgress.badges.length > 4 && (
                  <p className="text-center text-purple-600 text-sm mt-2">
                    +{userProgress.badges.length - 4} outras conquistas
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <button 
              className="w-full bg-gray-100 text-gray-700 py-3 rounded-2xl font-bold hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
              onClick={() => {
                alert('Configurações em desenvolvimento!');
              }}
            >
              <Settings className="w-5 h-5" />
              Configurações
            </button>
            
            <button 
              onClick={handleLogout}
              className="w-full bg-red-500 text-white py-3 rounded-2xl font-bold hover:bg-red-600 transition-colors flex items-center justify-center gap-2"
            >
              <LogOut className="w-5 h-5" />
              Sair da Conta
            </button>
          </div>
        </div>

        {/* Study Motivation */}
        <div className="mt-6 bg-gradient-to-r from-blue-500 to-purple-600 text-white p-6 rounded-2xl text-center">
          <h4 className="font-bold text-lg mb-2">Continue Estudando! 🎯</h4>
          <p className="text-blue-100 text-sm">
            Você já completou {userProgress.completed_lessons} lições. 
            Continue assim para desbloquear novos badges!
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;